import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PaginatedRepository } from '../pagination/paginated.repository';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { ReusableParty } from '../../interfaces/reusable-parties/reusable-party';
import { ReusablePartyForCreation } from '../../interfaces/reusable-parties/reusable-party-for-creation';
import { ReusablePartyForUpdate } from '../../interfaces/reusable-parties/reusable-party-for-update';
import { ReusablePartyLink } from '../../interfaces/reusable-parties//reusable-party-link';
import { ePartyLinkType } from '../../enums';
import { ReusablePartyLinkForCreation } from '../../interfaces/reusable-parties/reusable-party-link-for-creation';

@Injectable({
    providedIn: 'root'
})
export class ReusablePartyRepositoryService extends PaginatedRepository<ReusableParty> {
    protected override _baseLink: string;

    constructor(
        http: HttpClient,
        messageService: ToastrService,
        @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {

        super(http, messageService);

        this._currentParams = new HttpParams()
            .set('pageSize', 10);

        this._baseLinkTemplate = `${config.rootUrl}/Corporations/[0]/ReusableParties`;
        this._baseLink = this._baseLinkTemplate;
    }

    private generateHeaders = () => {
        return {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        }
    }

    public createReusableParty = (corporationID: string, reusableParty: ReusablePartyForCreation, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.post<ReusableParty>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties`,
            reusableParty,
            this.generateHeaders());
    }

    public getReusableParty = (corporationID: string, reusablePartyID: string, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.get<ReusableParty>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties/${reusablePartyID}`,
            this.generateHeaders());
    }

    public getReusableParties(corporationID: string, options?: { userGroupID?: string, pageSize?: number }) {
        var urlSlug = `/Corporations/${corporationID}`

        if (options?.userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${options?.userGroupID}`
        }

        return this.http.get<ReusableParty[]>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties?IsVisible=true`,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                params: new HttpParams().set("PageSize", options?.pageSize ?? 10)
            });
    }

    public updateReusableParty = (corporationID: string, reusablePartyID: string, reusableParty: ReusablePartyForUpdate, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.put<ReusablePartyForUpdate>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties/${reusablePartyID}`,
            reusableParty,
            this.generateHeaders());
    }

    public deleteReusableParty = (corporationID: string, reusablePartyID: string, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.delete(
            `${this.config.rootUrl}${urlSlug}/ReusableParties/${reusablePartyID}`,
            this.generateHeaders());
    }

    public linkPartiesByID(corporationID: string, parentPartyID: string, childPartyID: string) {
        let partyForCreation: ReusablePartyLinkForCreation = {
            partyLinkType: ePartyLinkType.association,
            reusablePartyID: parentPartyID,
            linkedReusablePartyID: childPartyID
        };

        return this.linkParties(corporationID, partyForCreation);
    }

    public linkParties(corporationID: string, partyLinkForCreation: ReusablePartyLinkForCreation) {
        this.populateBaseLink([corporationID]);

        return this.http.post<ReusablePartyLink>(
            `${this._baseLink}/${partyLinkForCreation.reusablePartyID}/LinkedParties`,
            partyLinkForCreation);
    }

    public unlinkParties(corporationID: string, reusablePartyID: string, reusablePartyLinkID: string) {
        this.populateBaseLink([corporationID]);

        return this.http.delete<ReusablePartyLink>(
            `${this._baseLink}/${reusablePartyID}/LinkedParties/${reusablePartyLinkID}`);
    }
}
